import { useEffect, useState } from 'react'

import { ExpandableMobileNav, Icon } from '@ministries/storybook'
import { useLocation, useMatches, useNavigate } from 'react-router'

import { useI18n } from '@app/utilities/i18n/provider'

import {
  type AppNavItem,
  getAppNavItems,
  isAppNavItemActive
} from './Navigation'

export default function Mobile() {
  const { hash, pathname } = useLocation()
  const matches = useMatches()
  const navigate = useNavigate()
  const { t } = useI18n()
  const rootLoaderData = matches
    .map(match => match.data)
    .find(
      (
        data
      ): data is {
        currentTenant?: { role: string } | null
      } => typeof data === 'object' && data !== null && 'currentTenant' in data
    )

  const currentUser = matches
    .map(match => match.data)
    .find(
      (data): data is { currentUser: { role: string } } =>
        typeof data === 'object' &&
        data !== null &&
        'currentUser' in data &&
        typeof data.currentUser === 'object' &&
        data.currentUser !== null &&
        'role' in data.currentUser
    )?.currentUser

  const navItems = getAppNavItems(
    {
      admin: t('home.navigation.admin'),
      coffee: t('home.navigation.coffee'),
      events: t('home.navigation.events'),
      features: t('home.navigation.features'),
      home: t('home.navigation.home'),
      music: t('home.navigation.music'),
      notifications: t('home.navigation.notifications'),
      profile: t('home.navigation.profile')
    },
    {
      isAdmin:
        rootLoaderData?.currentTenant?.role === 'owner' ||
        rootLoaderData?.currentTenant?.role === 'admin' ||
        currentUser?.role === 'admin'
    }
  )

  const [isExpanded, setIsExpanded] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const [isHidden, setIsHidden] = useState(false)

  useEffect(() => {
    setIsExpanded(false)
    setIsHidden(false)
  }, [hash, pathname])

  useEffect(() => {
    let lastScrollY = window.scrollY

    function handleScroll() {
      const nextScrollY = window.scrollY
      const nextHasScrolled = nextScrollY > 24
      const scrolledDown = nextScrollY > lastScrollY
      setHasScrolled(nextHasScrolled)

      if (nextHasScrolled) {
        setIsExpanded(false)
      }

      const shouldHide = scrolledDown && nextScrollY > 96

      if (shouldHide) {
        setIsHidden(true)
      }

      if (!scrolledDown || nextScrollY <= 24) {
        setIsHidden(false)
      }

      lastScrollY = nextScrollY
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  function handleItemNavigation(item: AppNavItem) {
    if (item.route === '/' && item.section && pathname === '/') {
      const target = document.querySelector(item.section)

      if (target instanceof HTMLElement) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }

      return
    }

    void navigate(item.route)
  }

  const activeItem =
    navItems.find(item => isAppNavItemActive(item, pathname, hash)) ??
    navItems[0]

  const isCompact = hasScrolled && !isExpanded
  const isVisibleExpanded = !hasScrolled || isExpanded
  const inactiveItems = navItems.filter(item => item !== activeItem)
  const leftItems = inactiveItems.slice(0, 2)
  const rightItems = inactiveItems.slice(2)

  return (
    <ExpandableMobileNav
      ariaLabel="Mobile app navigation"
      className={
        isHidden
          ? 'pointer-events-none translate-y-[calc(100%+1rem)] opacity-0'
          : 'translate-y-0 opacity-100'
      }
      centerButtonClassName="border-2 border-emerald-500 bg-white text-emerald-600 shadow-[0_0_0_4px_rgba(16,185,129,0.14)] hover:bg-emerald-50"
      centerContent={
        <Icon
          name={activeItem.icon}
          className="h-5 w-5 stroke-[2.1] text-emerald-600"
        />
      }
      collapseLabel={t('navigation.mobile.collapse')}
      expandLabel={t('navigation.mobile.expand')}
      isCompact={isCompact}
      isExpanded={isVisibleExpanded}
      leftActions={leftItems.map(item => ({
        id: item.section ?? item.route,
        icon: <Icon name={item.icon} />,
        label: item.label,
        onClick: () => handleItemNavigation(item)
      }))}
      onToggle={() => setIsExpanded(current => !current)}
      rightActions={rightItems.map(item => ({
        id: item.section ?? item.route,
        icon: <Icon name={item.icon} />,
        label: item.label,
        onClick: () => handleItemNavigation(item)
      }))}
    />
  )
}
